import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './ConnectEmail.css';

const introImages = [
  "firstimage.jpeg",
  "New York 🌟🗽.jpeg",
  "Astronaut's Last View of Home _ Receding Earth _ Emotional Depth Focus _ Sharp Helmet.jpeg",
  "Echoes of Tomorrow (@eoft_ai) on X.jpeg",
  "harry Potter.jpeg",
  "robot.jpg",
  "Steve Jobs.jpeg",
  "30 Women Pioneers Who Changed the World.jpeg",
  "united nations, nyc_.jpeg",
  "_ (5).jpeg",
  "_ (5) copy.jpeg",
  "_ (5) copy 2.jpeg",
  "_ copy.jpeg",
  "_ copy 2.jpeg",
  "_ copy 4.jpeg",
  "_ copy 5.jpeg",
  "_ copy 8.jpeg",
  "_ copy 10.jpeg"
];

// Preload images helper
const preloadImages = (srcArray) => {
  const promises = srcArray.map((src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
          resolve();
      };
      img.onerror = () => {
          console.error("Failed to load:", src);
          resolve(); // Resolve anyway to avoid blocking
      };
    });
  });
  return Promise.all(promises);
};

/**
 * NOTE:
 * This page used to initiate Google OAuth with Gmail/Calendar/Contacts scopes.
 * Those sensitive scopes trigger Google's "App hasn't been verified" warning unless the OAuth app is verified.
 *
 * The user request is Google-only login, but without the sensitive scopes. So we use vanilla Google Sign-In
 * (openid/email/profile) and do NOT request Gmail/Calendar/Contacts scopes here.
 */
const ConnectEmail = () => {
  const navigate = useNavigate();
  const { user, hasDoneOnboarding, loading: authLoading, signInWithGoogle, signInWithMagicLink, signOut } = useAuth();
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [showEmailInput, setShowEmailInput] = useState(false);
    const [magicLinkSent, setMagicLinkSent] = useState(false);

    const handleMagicLinkLogin = async (e) => {
        e.preventDefault();
        if (!email) return;

        setLoading(true);
        try {
            const { error } = await signInWithMagicLink(email);
            if (error) {
                return;
            }

            // Show inline confirmation instead of toast
            setMagicLinkSent(true);
            setShowEmailInput(false);
        } finally {
            setLoading(false);
        }
    };
  
  // Show intro if not seen in this session AND user is not already logged in
  const [showIntro, setShowIntro] = useState(() => {
    // If user is already logged in (re-render), don't show intro
    if (user) return false;
    // Otherwise check session storage
    return !sessionStorage.getItem('fig_intro_seen');
  });
  
  const [isExiting, setIsExiting] = useState(false); // New state for fade-out
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Mark intro as seen when it mounts/plays
  useEffect(() => {
    if (showIntro) {
      sessionStorage.setItem('fig_intro_seen', 'true');
    }
  }, [showIntro]);

  // Skip intro on click or keyboard press
  const skipIntro = useCallback(() => {
    if (!showIntro || isExiting) return;
    setIsExiting(true);
    setTimeout(() => {
      setShowIntro(false);
    }, 300); // Faster fade out when skipped
  }, [showIntro, isExiting]);

  // Handle click to skip
  useEffect(() => {
    if (!showIntro || isExiting) return;
    
    const handleClick = () => skipIntro();
    const handleKeyPress = () => skipIntro();
    
    window.addEventListener('click', handleClick);
    window.addEventListener('keydown', handleKeyPress);
    
    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [showIntro, isExiting, skipIntro]);

  // Timer Logic - Total sequence duration: 1.5 seconds with original pace
  useEffect(() => {
    // If we're already exiting, don't run the image timer
    if (!showIntro || isExiting) return;

    const totalDuration = 1500; // 1.5 seconds total
    const intervalTime = 111; // Original pace (~111ms per image from 2000ms / 18 images)

    const interval = setInterval(() => {
        setCurrentImageIndex(prev => {
            const next = prev + 1;
            // Stop if we've shown all images
            if (next >= introImages.length) {
                clearInterval(interval);
                // Start fade out sequence
                setIsExiting(true);
                // Remove from DOM after transition matches CSS duration
                setTimeout(() => {
                    setShowIntro(false);
                }, 1000); // 1s fade out
                return prev; 
            }
            return next;
        });
    }, intervalTime);

    // Stop the carousel after totalDuration (1.5 seconds)
    const timeout = setTimeout(() => {
        clearInterval(interval);
        setIsExiting(true);
        setTimeout(() => {
            setShowIntro(false);
        }, 1000); // 1s fade out
    }, totalDuration);
    
    return () => {
        clearInterval(interval);
        clearTimeout(timeout);
    };
  }, [showIntro, isExiting]);

  // Prevent Redirect until Intro Done + Delay
  useEffect(() => {
    // Debug log

    if (showIntro) return; // Wait for intro to fully unmount

    if (authLoading) return;
    
    if (user) {
      // Check if we were sent here from a specific destination (e.g. /writer)
      const postLoginRedirect = sessionStorage.getItem('postLoginRedirect');
      const destination = postLoginRedirect || '/data-privacy';
      if (postLoginRedirect) sessionStorage.removeItem('postLoginRedirect');

      // Add a small delay so user actually SEES the "Welcome to Fig" screen
      // before being whisked away.
      const timer = setTimeout(() => {
          navigate(destination, { replace: true });
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [authLoading, user, hasDoneOnboarding, navigate, showIntro]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) return;
      // Redirect happens via Supabase OAuth; on return, auth state will route user onward.
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
  };

  return (
    <>
      {/* Intro Animation Overlay */}
      {showIntro && (
        <div 
            className={`fixed inset-0 bg-white z-[9999] transition-opacity duration-1000 ease-in-out ${isExiting ? 'opacity-0' : 'opacity-100'} flex items-center justify-center cursor-pointer`}
            onClick={skipIntro}
        >
            <div className="flex flex-col items-center gap-6 sm:gap-10">
                {/* Phone-sized vertical image container */}
                <div className="relative w-[240px] h-[480px] sm:w-[320px] sm:h-[640px] rounded-[2.5rem] overflow-hidden shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15)] border-[3px] border-slate-200">
                    {introImages.map((imageName, index) => (
                        <img 
                            key={imageName} 
                            src={`/Fig Intro Pics/${encodeURIComponent(imageName)}`}
                            alt={`Intro sequence ${index + 1}`}
                            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-150 ease-in-out"
                            style={{ 
                                opacity: index === currentImageIndex ? 1 : 0,
                                zIndex: index
                            }}
                        />
                    ))}
                    {/* Subtle inner border */}
                    <div className="absolute inset-0 pointer-events-none border-[1px] border-slate-100 rounded-[2.5rem]"></div>
                </div>
                
                {/* FIG CAREER text centered under image */}
                <div className="w-[240px] sm:w-[320px] flex flex-col items-center gap-2 sm:gap-3">
                    <h1
                        className="text-slate-800 text-xl sm:text-[1.75rem] font-normal uppercase leading-tight whitespace-nowrap"
                        style={{
                            letterSpacing: '0.35em',
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 400
                        }}
                    >
                        F I G &nbsp;&nbsp; C A R E E R
                    </h1>
                    <p
                        className="text-slate-500 text-sm text-center"
                        style={{
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 400
                        }}
                    >
                        The career copilot that helps you figure out what's next.
                    </p>
                </div>
            </div>
        </div>
      )}

      {/* Main Connect Email Screen */}
      <div className="connect-email-container">
        <div className="content-wrapper">
          <div className="step-content fade-in">
            <h1 className="step-title">Welcome to Fig</h1>
            <div className="logo-container">
              <img src="/figlogo.png" alt="Fig Logo" width="120" height="120" style={{ objectFit: 'contain' }} />
            </div>

            <div className="privacy-section">
              <p className="privacy-intro" style={{ marginBottom: '12px' }}>
                Fig Careers is an AI-powered career guidance platform that creates personalized roadmaps based on your skills, interests, and aspirations.
              </p>
              <p className="privacy-intro">Sign in to start your journey.</p>
            </div>

            {user ? (
              <div className="accounts-list">
                <div className="account-card connected">
                  <div className="account-info">
                    <div className="account-avatar">{user.email ? user.email.charAt(0).toUpperCase() : '?'}</div>
                    <span className="account-email">{user.email || 'Unknown Email'}</span>
                  </div>
                  <span className="badge-primary">Signed in</span>
                </div>
              </div>
            ) : (
              <div className="w-full max-w-sm mx-auto mt-4 px-4 space-y-4">
                <button 
                    type="button"
                    className="w-full bg-white border-2 border-slate-200 text-slate-700 font-semibold py-4 px-4 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-3 shadow-sm hover:shadow-md"
                    onClick={handleGoogleLogin} 
                    disabled={loading}
                >
                    <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                    <span>Continue with Google</span>
                </button>

                <div className="relative flex items-center justify-center">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-200"></span>
                  </div>
                  <span className="relative bg-white px-2 text-sm text-slate-400">Or</span>
                </div>

                {magicLinkSent ? (
                    <div className="bg-green-50 border border-green-100 rounded-xl p-6 text-center animate-in fade-in zoom-in duration-300">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h3 className="text-green-800 font-semibold mb-1">Check your inbox!</h3>
                        <p className="text-green-700 text-sm mb-4">
                            We sent a magic login link to <span className="font-medium">{email}</span>
                        </p>
                        <button 
                            onClick={() => {
                                setMagicLinkSent(false);
                                setEmail('');
                            }}
                            className="text-xs text-green-600 underline hover:text-green-800"
                        >
                            Use a different email
                        </button>
                    </div>
                ) : !showEmailInput ? (
                  <button
                    type="button" 
                    onClick={() => setShowEmailInput(true)}
                    className="w-full bg-slate-50 text-slate-600 font-semibold py-3 px-4 rounded-xl hover:bg-slate-100 transition-all text-sm"
                  >
                    Continue with Email
                  </button>
                ) : (
                  <form onSubmit={handleMagicLinkLogin} className="space-y-3 animate-in fade-in slide-in-from-top-2">
                    <input
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-200 transition-all"
                      required
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-slate-900 text-white font-semibold py-3 px-4 rounded-xl hover:bg-slate-800 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Sending...' : 'Send Magic Link'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEmailInput(false)}
                      className="w-full text-slate-400 text-xs hover:text-slate-600"
                    >
                      Cancel
                    </button>
                  </form>
                )}
                
                {/* Computer screen note - right below Google sign-in */}
                <p className="text-center text-black text-xl font-medium mt-6">
                  Best experienced on a computer screen
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="footer-actions">
          <div className="footer-links" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            {user && (
              <button onClick={handleLogout}>Log out</button>
            )}
            <a
              href="/privacy"
              style={{
                color: '#64748b',
                fontSize: '12px',
                textDecoration: 'underline',
                marginTop: user ? '0' : '8px'
              }}
            >
              Privacy Policy
            </a>
            <span style={{ color: '#94a3b8', fontSize: '11px' }}>
              &copy; 2026 Fig Careers
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConnectEmail;
